/**
 * Chaves de React Query centralizadas (CLAUDE.md 8).
 *
 * Chave montada na mao dentro do componente e a origem classica de cache que
 * nao invalida: a mutacao invalida `["patients"]` e a lista escutava
 * `["patient", "list"]`. Com tudo aqui, invalidacao e busca usam a mesma
 * fonte.
 *
 * Toda chave e parametrizada pelo tenant: trocar de clinica (suporte via
 * X-Admin-Tenant-Slug) nao pode servir o cache da anterior.
 */
export const queryKeys = {
  session: (scope: "user" | "operator" = "user") => ["session", scope] as const,

  tenants: {
    all: ["admin", "tenants"] as const,
    list: (filters: Record<string, unknown> = {}) => ["admin", "tenants", "list", filters] as const,
    detail: (id: string) => ["admin", "tenants", "detail", id] as const,
    config: (id: string) => ["admin", "tenants", "detail", id, "config"] as const,
    users: (id: string) => ["admin", "tenants", "detail", id, "users"] as const,
    webhooks: (id: string) => ["admin", "tenants", "detail", id, "webhooks"] as const,
    webhookDeliveries: (id: string, endpointId: string) =>
      ["admin", "tenants", "detail", id, "webhooks", endpointId, "deliveries"] as const,
  },

  platformConfig: ["admin", "platform-config"] as const,

  operators: {
    all: ["admin", "operators"] as const,
  },

  patients: {
    all: ["patients"] as const,
    list: (filters: Record<string, unknown> = {}) => ["patients", "list", filters] as const,
    detail: (id: string) => ["patients", "detail", id] as const,
    summary: (id: string) => ["patients", "detail", id, "summary"] as const,
    evolution: (id: string, days: number) => ["patients", "detail", id, "evolution", days] as const,
  },

  dailyRecords: {
    today: (patientId: string) => ["patients", "detail", patientId, "daily-records", "today"] as const,
    list: (patientId: string, filters: Record<string, unknown> = {}) =>
      ["patients", "detail", patientId, "daily-records", "list", filters] as const,
  },

  dashboard: (days?: number) => ["dashboard", days ?? "default"] as const,

  orientations: {
    list: (patientId: string) => ["patients", "detail", patientId, "orientations", "list"] as const,
    detail: (patientId: string, orientationId: string) =>
      ["patients", "detail", patientId, "orientations", "detail", orientationId] as const,
  },

  orientationTemplates: {
    all: ["orientation-templates"] as const,
  },

  messages: {
    list: (patientId: string) => ["patients", "detail", patientId, "messages", "list"] as const,
  },

  alerts: {
    list: (filters: Record<string, unknown> = {}) => ["alerts", "list", filters] as const,
  },

  notifications: {
    list: (filters: Record<string, unknown> = {}) => ["notifications", "list", filters] as const,
  },

  patientNotes: {
    list: (patientId: string) => ["patients", "detail", patientId, "notes", "list"] as const,
  },

  team: {
    list: (patientId: string) => ["patients", "detail", patientId, "team", "list"] as const,
  },

  reminder: ["reminder"] as const,

  goals: {
    list: (patientId: string) => ["patients", "detail", patientId, "goals", "list"] as const,
  },

  reports: {
    list: (patientId: string) => ["patients", "detail", patientId, "reports", "list"] as const,
    detail: (patientId: string, reportId: string) =>
      ["patients", "detail", patientId, "reports", "detail", reportId] as const,
  },

  voiceCheckin: {
    availability: ["voice-checkin", "availability"] as const,
  },

  shares: {
    list: (patientId: string) => ["patients", "detail", patientId, "shares", "list"] as const,
    shared: (token: string) => ["shared", token] as const,
  },

  imports: {
    all: ["imports"] as const,
    list: ["imports", "list"] as const,
    detail: (id: string) => ["imports", "detail", id] as const,
  },

  calendarEvents: {
    list: (filters: Record<string, unknown> = {}) => ["calendar-events", "list", filters] as const,
  },
} as const;
